import React, { useState, useEffect } from 'react';
import { 
  fetchContextosIa, 
  createContextoIa, 
  updateContextoIa, 
  deleteContextoIa, 
  toggleContextoIaActive,
  duplicateContextoIa,
  fetchMaterias,
  fetchAnosEscolares,
  fetchContextosByFilters
} from '../services/contextosIaService';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Power, 
  PowerOff, 
  Search,
  Brain,
  BookOpen,
  Calendar,
  Filter,
  X
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/contextos-ia.css';

const ContextosIa = () => {
  const [contextos, setContextos] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [anosEscolares, setAnosEscolares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContexto, setEditingContexto] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    materia_id: '',
    ano_escolar_id: '',
    contexto: '',
    ativo: true
  });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    materia_id: '',
    ano_escolar_id: '',
    ativo: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    ativos: 0,
    inativos: 0
  });

  // Configuração do ReactQuill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'blockquote', 'code-block'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background',
    'link', 'blockquote', 'code-block'
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contextosData, materiasData, anosData] = await Promise.all([
        fetchContextosIa(),
        fetchMaterias(),
        fetchAnosEscolares()
      ]);
      
      setContextos(contextosData);
      setMaterias(materiasData);
      setAnosEscolares(anosData);
      calculateStats(contextosData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      const filterParams = {
        ...filters,
        search: searchTerm
      };

      // Remove filtros vazios
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '') {
          delete filterParams[key];
        }
      });

      const filteredData = await fetchContextosByFilters(filterParams);
      setContextos(filteredData);
      calculateStats(filteredData);
    } catch (error) {
      console.error('Erro ao aplicar filtros:', error);
    }
  };

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      ativos: data.filter(item => item.ativo).length,
      inativos: data.filter(item => !item.ativo).length
    };
    setStats(stats);
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      materia_id: '',
      ano_escolar_id: '',
      contexto: '',
      ativo: true
    });
    setErrors({});
    setEditingContexto(null);
  };

  const openModal = (contexto = null) => {
    if (contexto) {
      setEditingContexto(contexto);
      setFormData({
        titulo: contexto.titulo || '',
        materia_id: contexto.materia_id || '',
        ano_escolar_id: contexto.ano_escolar_id || '',
        contexto: contexto.contexto || '',
        ativo: contexto.ativo !== undefined ? contexto.ativo : true
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'Título é obrigatório';
    }

    if (!formData.materia_id) {
      newErrors.materia_id = 'Matéria é obrigatória';
    }

    if (!formData.ano_escolar_id) {
      newErrors.ano_escolar_id = 'Ano escolar é obrigatório';
    }

    if (!formData.contexto.trim()) {
      newErrors.contexto = 'Contexto é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (editingContexto) {
        await updateContextoIa(editingContexto.id, formData);
        alert('Contexto de IA atualizado com sucesso!');
      } else {
        await createContextoIa(formData);
        alert('Contexto de IA criado com sucesso!');
      }

      closeModal();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar contexto:', error);
      alert('Erro ao salvar contexto. Tente novamente.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este contexto de IA?')) {
      try {
        await deleteContextoIa(id);
        alert('Contexto de IA excluído com sucesso!');
        loadData();
      } catch (error) {
        console.error('Erro ao excluir contexto:', error);
        alert('Erro ao excluir contexto. Tente novamente.');
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await toggleContextoIaActive(id, !currentStatus);
      loadData();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status. Tente novamente.');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicateContextoIa(id);
      alert('Contexto de IA duplicado com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao duplicar contexto:', error);
      alert('Erro ao duplicar contexto. Tente novamente.');
    }
  };

  const clearFilters = () => {
    setFilters({
      materia_id: '',
      ano_escolar_id: '',
      ativo: ''
    });
    setSearchTerm('');
  };

  const getStatusBadge = (ativo) => {
    return (
      <span className={`status-badge ${ativo ? 'ativo' : 'inativo'}`}>
        {ativo ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="contextos-ia-container">
        <div className="loading">Carregando contextos de IA...</div>
      </div>
    );
  }

  return (
    <div className="contextos-ia-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <Brain className="header-icon" />
            <h1>Contextos de IA</h1>
          </div>
          <button className="btn-primary" onClick={() => openModal()}>
            <Plus size={20} />
            Novo Contexto
          </button>
        </div>

        {/* Estatísticas */}
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.ativos}</div>
            <div className="stat-label">Ativos</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.inativos}</div>
            <div className="stat-label">Inativos</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="filters-section">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por título ou contexto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <button 
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={20} />
            Filtros
          </button>

          {showFilters && (
            <div className="filters-container">
              <select
                value={filters.materia_id}
                onChange={(e) => setFilters({...filters, materia_id: e.target.value})}
                className="filter-select"
              >
                <option value="">Todas as matérias</option>
                {materias.map(materia => (
                  <option key={materia.id} value={materia.id}>
                    {materia.nome}
                  </option>
                ))}
              </select>

              <select
                value={filters.ano_escolar_id}
                onChange={(e) => setFilters({...filters, ano_escolar_id: e.target.value})}
                className="filter-select"
              >
                <option value="">Todos os anos</option>
                {anosEscolares.map(ano => (
                  <option key={ano.id} value={ano.id}>
                    {ano.nome}
                  </option>
                ))}
              </select>

              <select
                value={filters.ativo}
                onChange={(e) => setFilters({...filters, ativo: e.target.value})}
                className="filter-select"
              >
                <option value="">Todos os status</option>
                <option value="true">Apenas ativos</option>
                <option value="false">Apenas inativos</option>
              </select>

              <button className="btn-secondary" onClick={clearFilters}>
                <X size={16} />
                Limpar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lista de Contextos */}
      <div className="contextos-grid">
        {contextos.length === 0 ? (
          <div className="empty-state">
            <Brain size={64} />
            <h3>Nenhum contexto de IA encontrado</h3>
            <p>Crie o primeiro contexto para começar.</p>
          </div>
        ) : (
          contextos.map(contexto => (
            <div key={contexto.id} className={`contexto-card ${!contexto.ativo ? 'inactive' : ''}`}>
              <div className="card-header">
                <h3 className="card-title">{contexto.titulo}</h3>
                {getStatusBadge(contexto.ativo)}
              </div>

              <div className="card-info">
                <div className="info-item">
                  <BookOpen size={16} />
                  <span>{contexto.materias?.nome || 'Matéria não encontrada'}</span>
                </div>
                <div className="info-item">
                  <Calendar size={16} />
                  <span>{contexto.anos_escolares?.nome || 'Ano não encontrado'}</span>
                </div>
              </div>

              <div className="card-content">
                <div 
                  className="contexto-preview"
                  dangerouslySetInnerHTML={{ 
                    __html: contexto.contexto?.substring(0, 200) + (contexto.contexto?.length > 200 ? '...' : '') 
                  }}
                />
              </div>

              <div className="card-actions">
                <button
                  className="btn-icon"
                  onClick={() => openModal(contexto)}
                  title="Editar"
                >
                  <Edit size={16} />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleDuplicate(contexto.id)}
                  title="Duplicar"
                >
                  <Copy size={16} />
                </button>
                <button
                  className={`btn-icon ${contexto.ativo ? 'active' : 'inactive'}`}
                  onClick={() => handleToggleActive(contexto.id, contexto.ativo)}
                  title={contexto.ativo ? 'Desativar' : 'Ativar'}
                >
                  {contexto.ativo ? <Power size={16} /> : <PowerOff size={16} />}
                </button>
                <button
                  className="btn-icon delete"
                  onClick={() => handleDelete(contexto.id)}
                  title="Excluir"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="card-footer">
                <small>
                  Criado em: {new Date(contexto.created_at).toLocaleDateString('pt-BR')}
                </small>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingContexto ? 'Editar Contexto de IA' : 'Novo Contexto de IA'}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h3>Informações Básicas</h3>
                
                <div className="form-group">
                  <label htmlFor="titulo">Título *</label>
                  <input
                    type="text"
                    id="titulo"
                    value={formData.titulo}
                    onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                    className={errors.titulo ? 'error' : ''}
                    placeholder="Digite o título do contexto"
                  />
                  {errors.titulo && <span className="error-message">{errors.titulo}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="materia_id">Matéria *</label>
                    <select
                      id="materia_id"
                      value={formData.materia_id}
                      onChange={(e) => setFormData({...formData, materia_id: e.target.value})}
                      className={errors.materia_id ? 'error' : ''}
                    >
                      <option value="">Selecione uma matéria</option>
                      {materias.map(materia => (
                        <option key={materia.id} value={materia.id}>
                          {materia.nome}
                        </option>
                      ))}
                    </select>
                    {errors.materia_id && <span className="error-message">{errors.materia_id}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="ano_escolar_id">Ano Escolar *</label>
                    <select
                      id="ano_escolar_id"
                      value={formData.ano_escolar_id}
                      onChange={(e) => setFormData({...formData, ano_escolar_id: e.target.value})}
                      className={errors.ano_escolar_id ? 'error' : ''}
                    >
                      <option value="">Selecione um ano</option>
                      {anosEscolares.map(ano => (
                        <option key={ano.id} value={ano.id}>
                          {ano.nome}
                        </option>
                      ))}
                    </select>
                    {errors.ano_escolar_id && <span className="error-message">{errors.ano_escolar_id}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.ativo}
                      onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                    />
                    <span className="checkbox-text">Contexto ativo</span>
                  </label>
                </div>
              </div>

              <div className="form-section">
                <h3>Contexto de IA</h3>
                
                <div className="form-group">
                  <label>Contexto *</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.contexto}
                    onChange={(value) => setFormData({...formData, contexto: value})}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Digite o contexto que será usado pela IA..."
                    className={errors.contexto ? 'error' : ''}
                  />
                  {errors.contexto && <span className="error-message">{errors.contexto}</span>}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingContexto ? 'Atualizar' : 'Criar'} Contexto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextosIa;
